import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Attachments, {
  Attachment,
  AttachmentDraft,
  AttachmentsHeader,
  ConnectedAttachmentsDropzone,
} from "@adsk/acc-attachments";
import { PaperclipIcon } from "@adsk/alloy-react-icon";
import Badge from "@adsk/alloy-react-badge";
import Theme from "@adsk/alloy-react-theme";
import { Issue, PermittedAction } from "@adsk/acc-issues-types";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import {
  fetchIssueAttachmentsAction,
  addIssueAttachmentsAction,
  getPaginatedIssueAttachments,
  getTotalNumOfIssueAttachments,
  getShouldDisplayAttachmentLoadMore,
  getIsIssueAttachmentsError,
  getIsIssueAttachmentsLoading,
  removeIssueAttachmentsAction,
  loadMoreIssueAttachmentsAction,
} from "@adsk/acc-issues-core/state/redux/ducks/issueAttachments";
import {
  getAttachmentRequestData,
  getCustomDraftAttachment,
  CustomAttachmentDraft,
} from "../../utils/data/attachmentsHelpers";
import styled from "styled-components";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import {
  trackIssueAttachmentClick,
  trackIssueAttachmentAdd,
  trackIssueAttachmentRemove,
} from "../../utils/analytics/issueActionsTracking";
import { MAX_ALLOWED_ATTACHMENTS } from "../../constants/attachments";

const AttachmentsWrapper = styled.div<{
  shouldHaveMarginTop: boolean;
  error: boolean;
}>`
  margin-top: ${props => (props.shouldHaveMarginTop ? "16px" : 0)};
  border: 1px solid
    ${props => (props.error ? Theme.colors.red500 : "transparent")};
  border-radius: 4px;
`;

const CustomHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  height: 64px;
  border-bottom: 1px solid ${Theme.colors.charcoal200};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeletedIssueMessage = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  padding: 26px 60px;
  color: ${Theme.colors.charcoal700};
`;

const MIN_AMOUNT_OF_ATTACHMENTS_WITHOUT_SCROLL = 4;

enum AttachmentAnalytics {
  Add = "add",
  Remove = "remove",
  Click = "click",
}

type InteractedAttachmentTypes = {
  [key: string]: string;
};

export const AttachmentsField = ({
  issue,
  isDeletedIssue,
  onChange,
  error = false,
}: {
  issue: Issue | null;
  isDeletedIssue: boolean;
  onChange?: (draftAttachments: CustomAttachmentDraft[]) => void;
  error?: boolean;
}) => {
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const interactedAttachmentTypes = useRef<InteractedAttachmentTypes>({});
  const attachments = useSelector(getPaginatedIssueAttachments)(issue?.id);
  const totalNumOfIssueAttachments = useSelector(getTotalNumOfIssueAttachments)(
    issue?.id
  );
  const shouldDisplayLoadMore = useSelector(getShouldDisplayAttachmentLoadMore)(
    issue?.id
  );
  const [draftAttachments, setDraftAttachments] = useState<
    CustomAttachmentDraft[]
  >([]);

  const { value: shouldShowAttachmentsNewBadge } = useFeatureFlag(
    FeatureFlag.ShowAttachmentsNewBadge
  );

  const isInIssueCreation = !issue;

  const isAllowedDelete = useMemo(() => {
    if (isDeletedIssue) return false;
    return isInIssueCreation
      ? true
      : issue.permittedActions.includes(PermittedAction.RemoveAttachment);
  }, [issue, isDeletedIssue, isInIssueCreation]);

  const isAllowedAdd = useMemo(() => {
    if (isDeletedIssue) return false;
    return isInIssueCreation
      ? true
      : issue.permittedActions.includes(PermittedAction.AddAttachment);
  }, [issue, isDeletedIssue, isInIssueCreation]);

  const isLoading = useSelector(getIsIssueAttachmentsLoading)(issue?.id);
  const isError = useSelector(getIsIssueAttachmentsError)(issue?.id);

  const shouldFetchAttachments = useMemo(() => {
    return isInIssueCreation ? false : issue.attachmentCount > 0;
  }, [issue, isInIssueCreation]);

  const fetchAttachments = useCallback(async () => {
    if (!issue) return;
    await dispatch(fetchIssueAttachmentsAction(issue.id));
  }, [issue, dispatch]);

  useEffect(() => {
    if (shouldFetchAttachments) {
      fetchAttachments();
    }
  }, [dispatch, issue, fetchAttachments, shouldFetchAttachments]);

  const handleLoadMoreClick = useCallback(() => {
    if (!issue) return;
    dispatch(loadMoreIssueAttachmentsAction(issue.id));
  }, [dispatch, issue]);

  const fireAttachmentAnalytics = useCallback(
    (attachmentId, analyticType: AttachmentAnalytics) => {
      if (!interactedAttachmentTypes.current[attachmentId]) return;
      const analyticsData = {
        attachmentFileType: interactedAttachmentTypes.current[attachmentId],
        issueId: issue?.id || "",
      };
      switch (analyticType) {
        case AttachmentAnalytics.Add:
          trackIssueAttachmentAdd(analyticsData);
          break;
        case AttachmentAnalytics.Remove:
          trackIssueAttachmentRemove(analyticsData);
          break;
        case AttachmentAnalytics.Click:
          trackIssueAttachmentClick(analyticsData);
          break;
        default:
          break;
      }
      delete interactedAttachmentTypes.current[attachmentId];
    },
    [interactedAttachmentTypes, issue?.id]
  );

  const getFileType = (fileName: string) => {
    const parts = fileName.split(".");
    return parts[parts.length - 1];
  };

  const handleLinkAttachment = useCallback(
    async (unlinkedAttachment: AttachmentDraft) => {
      interactedAttachmentTypes.current[unlinkedAttachment.attachmentId] =
        getFileType(unlinkedAttachment.file.name);

      if (!issue) {
        const attachmentData = getCustomDraftAttachment(unlinkedAttachment);
        setDraftAttachments(prevDraftAttachments => {
          const newDrafts = [...prevDraftAttachments, attachmentData];
          onChange?.(newDrafts);
          return newDrafts;
        });
        return;
      }

      await dispatch(
        addIssueAttachmentsAction({
          issueId: issue.id,
          newAttachmentData: getAttachmentRequestData(
            issue.id,
            unlinkedAttachment
          ),
        })
      );

      fireAttachmentAnalytics(
        unlinkedAttachment.attachmentId,
        AttachmentAnalytics.Add
      );
    },
    [dispatch, issue, fireAttachmentAnalytics, onChange]
  );

  const handleUnlinkAttachment = useCallback(
    async ({ attachmentId }: { attachmentId: string }) => {
      if (!issue) return;
      const attachmentToRemove = attachments.find(
        (att: Attachment) => att.attachmentId === attachmentId
      );
      if (attachmentToRemove) {
        interactedAttachmentTypes.current[attachmentToRemove.attachmentId] =
          getFileType(attachmentToRemove.fileType);
      }
      await dispatch(
        removeIssueAttachmentsAction({
          issueId: issue.id,
          attachmentId,
        })
      );

      fireAttachmentAnalytics(
        attachmentToRemove.attachmentId,
        AttachmentAnalytics.Remove
      );
    },
    [attachments, dispatch, issue, fireAttachmentAnalytics]
  );

  const handleDeleteDraft = useCallback(
    draftId => {
      setDraftAttachments(prevDraftAttachments => {
        const newDrafts = prevDraftAttachments.filter(
          attachment => attachment.attachmentId !== draftId
        );
        onChange?.(newDrafts);
        return newDrafts;
      });
    },
    [onChange]
  );

  const handleDeleteSuccess = useCallback(() => {
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate("attachmentsField.deleteSuccessMessage"),
    });
  }, [T, showNotification]);

  const handleDeleteFailed = useCallback(() => {
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate("attachmentsField.deleteErrorMessage"),
    });
  }, [T, showNotification]);

  const handleAttachmentClick = useCallback(
    ({ attachmentId, fileType }) => {
      interactedAttachmentTypes.current[attachmentId] = fileType;
      fireAttachmentAnalytics(attachmentId, AttachmentAnalytics.Click);
    },
    [fireAttachmentAnalytics]
  );

  const renderCustomDropzone = useCallback(() => {
    if (isDeletedIssue) {
      if (isDeletedIssue && !attachments.length) {
        return (
          <DeletedIssueMessage style={Theme.typography.bodyMedium}>
            {T.translate("attachmentsField.deletedIssueMessage")}
          </DeletedIssueMessage>
        );
      }
      return null;
    }
    if (!isAllowedAdd) {
      return null;
    }
    // Rendering the default Dropzone component from the Attachments library
    return (
      <ConnectedAttachmentsDropzone
        customDropzoneTitle={
          T.translate("attachmentsField.dropzoneTitle") as string
        }
      />
    );
  }, [isDeletedIssue, isAllowedAdd, attachments, T]);

  const attachmentsCount = useMemo(() => {
    if (isInIssueCreation) return draftAttachments.length;
    if (!attachments || isLoading) return 0;
    return totalNumOfIssueAttachments;
  }, [
    isInIssueCreation,
    attachments,
    isLoading,
    totalNumOfIssueAttachments,
    draftAttachments.length,
  ]);

  const customHeaderAttachmentsCount = useMemo(() => {
    return attachmentsCount ? ` (${attachmentsCount})` : "";
  }, [attachmentsCount]);

  const renderHeader = useCallback(() => {
    //Rebuilding the header only to add the "New" badge,
    //remove all of this once the badge should be removed or when the Attachments component supports it internally
    return () =>
      shouldShowAttachmentsNewBadge ? (
        <CustomHeaderContainer>
          <TitleContainer>
            <PaperclipIcon />
            <span style={{ ...Theme.typography.heading3 }}>
              {T.translate("attachmentsField.title")}
              {customHeaderAttachmentsCount}
            </span>
          </TitleContainer>
          <Badge color={Badge.COLORS.POSITIVE}>
            {T.translate("attachmentsField.badgeText")}
          </Badge>
        </CustomHeaderContainer>
      ) : (
        <AttachmentsHeader numberOfAttachments={attachmentsCount} />
      );
  }, [
    T,
    shouldShowAttachmentsNewBadge,
    customHeaderAttachmentsCount,
    attachmentsCount,
  ]);

  const listHeight = useMemo(() => {
    return attachments.length > MIN_AMOUNT_OF_ATTACHMENTS_WITHOUT_SCROLL
      ? 500
      : undefined;
  }, [attachments.length]);

  const listVariant = useMemo(() => {
    return isInIssueCreation ? "list" : undefined;
  }, [isInIssueCreation]);

  return (
    <AttachmentsWrapper
      data-testid="attachments-field"
      shouldHaveMarginTop={!isInIssueCreation}
      error={error}
    >
      <Attachments
        renderHeader={renderHeader()}
        renderDropzone={renderCustomDropzone}
        allowDelete={isAllowedDelete}
        attachments={attachments}
        handleLinkAttachment={handleLinkAttachment}
        loading={isInIssueCreation ? false : isLoading}
        errorLoadingAttachments={isInIssueCreation ? false : isError}
        onRetryLoadingAttachments={fetchAttachments}
        onDeleteAttachment={handleUnlinkAttachment}
        onDeleteDraft={handleDeleteDraft}
        onAfterDelete={handleDeleteSuccess}
        onDeleteFailed={handleDeleteFailed}
        onAttachmentClick={handleAttachmentClick}
        listHeight={listHeight}
        showLoadMore={shouldDisplayLoadMore}
        onLoadMore={handleLoadMoreClick}
        showVariantToggle={!isInIssueCreation}
        variant={listVariant}
        maxNumberOfAttachments={MAX_ALLOWED_ATTACHMENTS}
        totalAttachments={totalNumOfIssueAttachments}
      />
    </AttachmentsWrapper>
  );
};
